<template>
    <!-- Bulk uses the "Are you sure?" confirmation because it is a genuine pre-confirmation of
         how many of the selected entities will be acted on, and the per-entity choices the
         create-link modal collects have no meaning across a mixed selection. -->
    <ConfirmSendEntityRequestModal
        v-if="!entity"
        :entities="entities"
        :saving="saving"
        @close="close"
        @confirm="sendBulkDocumentRequests"
    />

    <RequestCreatedModal
        v-else-if="createdRequest"
        :entity="entity"
        :prior-verification-status="priorVerificationStatus"
        :reopened-count="createdRequest.requestedRequirements.length"
        :submission-link="createdRequest.submissionLink"
        :notification-suppressed="createdRequest.notificationSuppressed"
        @close="finish"
    />

    <!-- TODO PRODUCT-26539: design has not supplied a title for the failed case; this string is a
         placeholder, not verbatim copy from the handoff. It holds for both failures this panel
         covers, since neither creates a request. -->
    <TprmModal
        v-else-if="failureMessage"
        open
        additional-class-name="SendDocumentRequestProcedure__failureModal"
        title="Request not created"
        :subtitle="entity.displayName"
        @close="close"
    >
        <!-- Where the server supplied this sentence it owns it: it names the state the entity moved
             into between the dashboard's last fetch and the click, which the portal cannot work out
             for itself. -->
        <p data-test-id="SendDocumentRequestProcedure__failure">
            {{ failureMessage }}
        </p>
    </TprmModal>

    <CreateNewLinkModal
        v-else
        :entity="entity"
        :requirement-details="requirementDetails"
        :requirement-models="requirementModels"
        :credential-types="credentialTypes"
        :loading="loadingRequirementDetails"
        :saving="saving"
        @close="close"
        @confirm="createNewLink"
    />
</template>

<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import {
        SendDocumentRequestSuccess,
        VerificationRequestEntry,
    } from '@evidentid/rpweb-api-client/models';
    import { TprmCredentialType } from '@evidentid/tprm-portal-lib/models/common';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import {
        ComplianceStatus,
        EntityRequirementDetails,
        TprmRequirementModel,
        VerificationStatus,
    } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import TprmModal from '@/components/tprm-modal/TprmModal.vue';
    import { useDashboardStore } from '@/modules/dashboard/vuex/dashboard';
    import { useDecisioningCriteriaStore } from '@/modules/decisioning-criteria/store';
    import { RequirementDetailsListStatus, useEntityDetailsStore } from '@/modules/entity-details/store';
    import ConfirmSendEntityRequestModal
        from '@/modules/entity-request/components/ConfirmSendEntityRequestModal/ConfirmSendEntityRequestModal.vue';
    import CreateNewLinkModal, { CreateNewLinkPayload }
        from '@/modules/entity-request/components/CreateNewLinkModal/CreateNewLinkModal.vue';
    import RequestCreatedModal
        from '@/modules/entity-request/components/RequestCreatedModal/RequestCreatedModal.vue';
    import { SendRequestStatus, useEntityRequestStore } from '@/modules/entity-request/store';
    import { SendDocumentRequestResult } from '@/modules/entity-request/types';
    import {
        getRequirementModelsWithCountryLabel,
    } from '@/utils/get-requirement-models-with-country-label/getRequirementModelsWithCountryLabel';
    import { isEligibleForNewLink } from '@/utils/is-eligible-for-new-link/isEligibleForNewLink';

    // Shown for a failure that carries no server sentence of its own. Matches the wording the
    // dashboard puts in its own alert for the same case.
    const GENERIC_FAILURE = 'Your request has failed. Please try again.';

    const EMPTY_SEND_STATUS: SendRequestStatus = {
        status: OperationStatus.uninitialized,
        progress: 0,
        entityIds: [],
        totalCount: 0,
        successCount: 0,
        failureCount: 0,
        successOnRequestCount: 0,
        failedOnRequestCount: 0,
        successes: [],
        failures: [],
    };

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    @Component({
        components: {
            ConfirmSendEntityRequestModal,
            CreateNewLinkModal,
            RequestCreatedModal,
            TprmModal,
        },
        emits: [ 'abort', 'finish' ],
    })

    export default class SendDocumentRequestProcedure extends Vue {
        // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't warn
        // "Extraneous non-props attributes (uuid)" (see UploadEntityDocumentProcedure.vue).
        @Prop({ type: String, default: '' })
        private uuid?: string;

        @Prop({ type: String, default: '' })
        private rpName!: string;

        @Prop({ type: Array, default: () => [] })
        private entities!: Entity[];

        /** The entity's state before the request was created. Captured before sending, because the
         *  request moves every entity to Pending Submission, and the created-link copy is chosen
         *  from where the entity came from. */
        private sentEntities: Entity[] = [];

        private priorVerificationStatus: VerificationStatus | null = null;

        private createdRequest: SendDocumentRequestSuccess | null = null;

        private sendFailureMessage: string = '';

        private get entityRequestStore() {
            return useEntityRequestStore();
        }

        /** The create-link flow is per entity: a requirement selection and an email choice only
         *  mean something for one of them, so a multi-entity selection keeps the bulk
         *  confirmation.
         *
         *  A selection that narrows to a single eligible entity is one of them, whatever its
         *  size: there is exactly one entity to collect choices for, so it gets the requirement
         *  picker and the email toggle rather than a bulk send that offers neither. */
        private get entity(): Entity | null {
            if (this.entities.length === 1) {
                return this.entities[0];
            }
            const eligibleEntities = this.entities.filter(isEligibleForNewLink);
            return eligibleEntities.length === 1 ? eligibleEntities[0] : null;
        }

        private get saving(): boolean {
            return this.sendRequestStatus.status === OperationStatus.loading;
        }

        private get sendRequestStatus(): SendRequestStatus {
            return this.entityRequestStore.sendDocumentRequestStatus[this.rpName] || EMPTY_SEND_STATUS;
        }

        /** Only a compliant entity picks requirements, so only a compliant entity needs the list. */
        private get needsRequirementDetails(): boolean {
            return this.entity?.complianceStatus === ComplianceStatus.compliant;
        }

        private get requirementDetailsStatus(): RequirementDetailsListStatus | undefined {
            return useEntityDetailsStore().requirementDetailsListStatus[this.rpName];
        }

        private get requirementDetails(): EntityRequirementDetails[] {
            return this.requirementDetailsStatus?.list || [];
        }

        /** Anything short of a loaded list counts as loading, so the picker is never shown with a
         *  list belonging to whichever entity the details panel looked at last. An entity that
         *  fetches nothing is never loading, or its submit button would never enable. */
        private get loadingRequirementDetails(): boolean {
            return this.needsRequirementDetails && this.requirementDetailsStatus?.status !== OperationStatus.success;
        }

        /** Without the requirement list a compliant entity has nothing to pick from, so the picker
         *  cannot be shown and waiting for it is pointless. */
        private get requirementDetailsLoadFailed(): boolean {
            return this.needsRequirementDetails
                && this.requirementDetailsStatus?.status === OperationStatus.error;
        }

        /** A failure the user has to read before anything else can happen: the server refused the
         *  request, or the list the picker needs never arrived. */
        private get failureMessage(): string {
            if (this.sendFailureMessage) {
                return this.sendFailureMessage;
            }
            return this.requirementDetailsLoadFailed ? GENERIC_FAILURE : '';
        }

        private get credentialTypes(): TprmCredentialType[] {
            return useDashboardStore().credentialTypes?.list || [];
        }

        /** Resolved the same way the entity details panel resolves them, so a requirement is named
         *  identically in the modal and on the page behind it. */
        private get requirementModels(): TprmRequirementModel[] {
            if (!this.entity) {
                return [];
            }
            const decisioningCriteriaStore = useDecisioningCriteriaStore();
            return getRequirementModelsWithCountryLabel(
                this.entity,
                decisioningCriteriaStore.requirementModelsStatus[this.rpName]?.list || [],
                decisioningCriteriaStore.riskProfiles[this.rpName]?.list || [],
                useDashboardStore().categorizedEnumLabels,
            );
        }

        private mounted(): void {
            if (this.needsRequirementDetails) {
                useEntityDetailsStore().loadRequirementDetailsList({
                    rpName: this.rpName,
                    entityId: this.entity!.id,
                });
            }
        }

        private async createNewLink(payload: CreateNewLinkPayload): Promise<void> {
            const entity = this.entity!;
            this.sentEntities = [ entity ];
            this.priorVerificationStatus = entity.verificationStatus;
            const entry: VerificationRequestEntry = { insuredId: entity.id, notifyEntity: payload.notifyEntity };
            if (payload.requirements) {
                entry.requirements = payload.requirements;
            }

            await this.entityRequestStore.sendDocumentRequest({ rpName: this.rpName, entries: [ entry ] });

            const created = this.sendRequestStatus.successes.find((success) => success.insuredId === entity.id);
            if (created) {
                this.createdRequest = created;
                return;
            }
            const refusal = this.sendRequestStatus.failures.find((failure) => failure.element === entity.id);
            this.sendFailureMessage = refusal?.error?.message || GENERIC_FAILURE;
        }

        private async sendBulkDocumentRequests(): Promise<void> {
            const qualifiedEntities = this.entities.filter(isEligibleForNewLink);
            this.sentEntities = qualifiedEntities;
            await this.entityRequestStore.sendDocumentRequest({
                rpName: this.rpName,
                entries: qualifiedEntities.map((entity) => ({ insuredId: entity.id })),
            });
            this.finish();
        }

        /** Both paths narrow the selection first -- bulk to the eligible entities, the per-entity
         *  dialog to the one it is about -- and the dashboard counts whatever arrives here to tell
         *  the user how many requests went out.
         */
        private finish(): void {
            const result: SendDocumentRequestResult = {
                entities: this.sentEntities,
                alreadyConfirmed: Boolean(this.createdRequest),
            };
            this.$emit('finish', result);
        }

        /** Aborts rather than finishes, so the dashboard raises no alert: either nothing was sent,
         *  or the failure panel has already said why. The dashboard reloads on abort as well, which
         *  is what corrects an entity whose state went stale under the user. */
        private close(): void {
            this.entityRequestStore.clearSendDocumentRequestStatus({ rpName: this.rpName });
            this.$emit('abort');
        }
    }
</script>
